import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import * as Sentry from '@sentry/browser';

import { ObjectType } from '$app-web/types';
import { isObject } from '$app-web/utils';

interface CustomError extends Error {
    response?: AxiosResponse;
}

export function captureClientErrorsToSentry(axios: AxiosInstance): void {
    axios.interceptors.response.use(
        (response: AxiosResponse) => response,
        (error: AxiosError) => {
            const { response, config } = error;
            const { status, data } = response ?? {};

            if (response == null || status == null) {
                return Promise.reject(error);
            }

            const errorName = `Error ${status} from "${config?.url}" endpoint`;
            const customError: CustomError = new Error(errorName);

            customError.name = errorName;
            customError.response = response;
            if (isObject(data)) {
                const backendErrorExtraInfo = (data as ObjectType).error_extra_info;
                const backendErrorMessage = (data as ObjectType).error_message;

                customError.message = (backendErrorExtraInfo as string) || (backendErrorMessage as string) || errorName;
            }

            const request = {
                page_of_error: window.location.href,
                ...(config?.method?.toUpperCase() ? { method: config.method?.toUpperCase() } : {}),
                ...(config?.url ? { url: config.url } : {}),
                ...(config?.headers ? { request_headers: config.headers } : {}),
                ...(config?.data ? { request_data: config.data } : {}),
                ...(config?.params ? { request_params: config.params } : {}),
            };

            try {
                if (status === 400) {
                    Sentry.withScope(() => {
                        Sentry.captureException(customError, {
                            tags: { catch_in: 'Axios Interceptor', error_type: 'Bad Request (400)' },
                            extra: { request, response },
                        });
                    });
                } else if (status >= 500 && status < 600) {
                    Sentry.withScope(() => {
                        Sentry.captureException(customError, {
                            tags: { catch_in: 'Axios Interceptor', error_type: `Server Error (${status})` },
                            extra: { request, response },
                        });
                    });
                }
            } catch (captureError) {
                // eslint-disable-next-line no-console
                console.error('Error capturing client error to Sentry:', captureError);
            }

            throw customError;
        }
    );
}
